import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn, Input, Label } from '@ideoz/ui';

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/80">{children}</p>
  );
}

export function SectionCard({
  title,
  children,
  className,
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-md border border-border/60 bg-card px-3 py-2.5 space-y-2.5',
        className,
      )}
    >
      {title ? <SectionTitle>{title}</SectionTitle> : null}
      {children}
    </div>
  );
}

export function FieldRow({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

/** Label-in-field + trailing unit pill (inspector-style). */
export function UnitInputRow({
  label,
  unit,
  value,
  onChange,
  onBlur,
  disabled,
  inputClassName,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  inputClassName?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center rounded-md border border-input bg-background h-8 px-2 text-xs shadow-sm',
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      <span className="text-muted-foreground truncate max-w-[40%] mr-2">{label}</span>
      <input
        aria-label={`${label} value`}
        className={cn(
          'flex-1 min-w-0 bg-transparent outline-none border-none text-xs text-right',
          inputClassName,
        )}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
      />
      <span className="text-muted-foreground text-[11px] tabular-nums ml-1 shrink-0">{unit}</span>
    </div>
  );
}

function sidesEqual(t: string, r: string, b: string, l: string): boolean {
  const norm = (x: string) => (x || '0').trim();
  return norm(t) === norm(r) && norm(r) === norm(b) && norm(b) === norm(l);
}

function shorthandDisplay(t: string, r: string, b: string, l: string): string {
  if (sidesEqual(t, r, b, l)) return (t || '0').trim() || '0';
  return '';
}

/** Collapsible box model row with editable shorthand (all 4 sides) when collapsed. */
export function BoxMetricRow({
  title,
  defaultExpanded,
  top,
  right,
  bottom,
  left,
  onTopChange,
  onRightChange,
  onBottomChange,
  onLeftChange,
  onShorthandBlur,
  onSideBlur,
}: {
  title: string;
  defaultExpanded?: boolean;
  top: string;
  right: string;
  bottom: string;
  left: string;
  onTopChange: (v: string) => void;
  onRightChange: (v: string) => void;
  onBottomChange: (v: string) => void;
  onLeftChange: (v: string) => void;
  onShorthandBlur: (px: string) => void;
  onSideBlur: (side: 'Top' | 'Right' | 'Bottom' | 'Left', px: string) => void;
}) {
  const [open, setOpen] = React.useState(defaultExpanded === true);
  const equal = sidesEqual(top, right, bottom, left);
  const displayShorthand = shorthandDisplay(top, right, bottom, left);
  const [shorthandDraft, setShorthandDraft] = React.useState(displayShorthand || '0');

  React.useEffect(() => {
    if (equal) setShorthandDraft(displayShorthand || '0');
    else setShorthandDraft('');
  }, [top, right, bottom, left, equal, displayShorthand]);

  const shorthandLabel = equal ? '' : 'Mixed';

  return (
    <div className="rounded-md border border-border/60 bg-background/90 overflow-hidden">
      <div className="flex w-full items-center gap-2 px-3 py-2">
        <button
          type="button"
          className="text-[11px] font-medium text-foreground shrink-0"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {title}
        </button>
        <div className="flex flex-1 items-center gap-1 min-w-0 justify-end">
          {!open ? (
            <div className="flex items-center rounded-md border border-input bg-background h-7 px-2 text-xs shadow-sm max-w-[120px]">
              <input
                aria-label={`${title} all sides`}
                className="flex-1 min-w-0 bg-transparent outline-none border-none text-xs text-right tabular-nums"
                placeholder={shorthandLabel || '0'}
                value={shorthandDraft}
                onChange={(e) => setShorthandDraft(e.target.value)}
                onBlur={() => {
                  const v = shorthandDraft.trim();
                  if (!v || v === 'Mixed') return;
                  onShorthandBlur(v);
                  setOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
              <span className="text-muted-foreground text-[10px] ml-1 shrink-0">px</span>
            </div>
          ) : null}
          <button
            type="button"
            className="p-0.5 text-muted-foreground shrink-0"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          >
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {open ? (
        <div className="border-t border-border/50 px-2 py-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <UnitInputRow
              label="Top"
              unit="px"
              value={top}
              onChange={onTopChange}
              onBlur={() => onSideBlur('Top', top)}
            />
            <UnitInputRow
              label="Right"
              unit="px"
              value={right}
              onChange={onRightChange}
              onBlur={() => onSideBlur('Right', right)}
            />
            <UnitInputRow
              label="Bottom"
              unit="px"
              value={bottom}
              onChange={onBottomChange}
              onBlur={() => onSideBlur('Bottom', bottom)}
            />
            <UnitInputRow
              label="Left"
              unit="px"
              value={left}
              onChange={onLeftChange}
              onBlur={() => onSideBlur('Left', left)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export const selectCls =
  'h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
